import type {
  GetServiceAccountsParams,
  GetServiceAccountsResponse,
  ServiceAccount,
  ServiceAccountHistory,
  ServiceAccountHistoryParams,
  ServiceAccountTokenResponse,
} from '@kizen/api/service-account';
import type {
  DefaultError,
  InfiniteData,
  InfiniteQueryObserverOptions,
  QueryObserverOptions,
} from '@tanstack/query-core';
import type { AxiosInstance } from 'axios';
import type { Resource } from './resource';
import type {
  Drop,
  WithInfiniteSelect,
  WithParams,
  WithRequired,
} from './types';

import {
  getServiceAccount,
  getServiceAccounts,
  getServiceAccountHistory,
  getPrimaryToken,
  getSecondaryToken,
} from '@kizen/api/service-account';
import { getResource } from './resource';
import { curry1 } from './utils';

type ServiceAccountQuery<TData = ServiceAccount> = WithRequired<
  QueryObserverOptions<
    ServiceAccount,
    DefaultError,
    TData,
    ServiceAccount,
    [Resource<Readonly<['service-accounts', string]>>]
  >,
  'queryFn' | 'queryKey'
>;

export const serviceAccountQuery = <TData = ServiceAccount>(
  axios: AxiosInstance,
  id: string,
  options?: Omit<ServiceAccountQuery<TData>, 'queryFn' | 'queryKey'>
): ServiceAccountQuery<TData> => {
  const path = ['service-accounts', id] as const;
  const resource = getResource(path);

  return {
    queryKey: [resource],
    queryFn: (context) => {
      const {
        queryKey: [qk],
        signal,
      } = context;

      return getServiceAccount(axios, qk.path[1], { signal });
    },
    ...options,
  };
};

type PrimaryTokenQuery<TData = ServiceAccountTokenResponse> = WithRequired<
  QueryObserverOptions<
    ServiceAccountTokenResponse,
    DefaultError,
    TData,
    ServiceAccountTokenResponse,
    [Resource<Readonly<['service-accounts', string, 'api-tokens', 'primary']>>]
  >,
  'queryFn' | 'queryKey'
>;

export const primaryTokenQuery = <TData = ServiceAccountTokenResponse>(
  axios: AxiosInstance,
  id: string,
  options?: Omit<PrimaryTokenQuery<TData>, 'queryFn' | 'queryKey'>
): PrimaryTokenQuery<TData> => {
  const path = ['service-accounts', id, 'api-tokens', 'primary'] as const;
  const resource = getResource(path);

  return {
    queryKey: [resource],
    queryFn: (context) => {
      const {
        queryKey: [qk],
        signal,
      } = context;

      return getPrimaryToken(axios, qk.path[1], { signal });
    },
    ...options,
  };
};

type SecondaryTokenQuery<TData = ServiceAccountTokenResponse> = WithRequired<
  QueryObserverOptions<
    ServiceAccountTokenResponse,
    DefaultError,
    TData,
    ServiceAccountTokenResponse,
    [
      Resource<
        Readonly<['service-accounts', string, 'api-tokens', 'secondary']>
      >,
    ]
  >,
  'queryFn' | 'queryKey'
>;

export const secondaryTokenQuery = <TData = ServiceAccountTokenResponse>(
  axios: AxiosInstance,
  id: string,
  options?: Omit<SecondaryTokenQuery<TData>, 'queryFn' | 'queryKey'>
): SecondaryTokenQuery<TData> => {
  const path = ['service-accounts', id, 'api-tokens', 'secondary'] as const;
  const resource = getResource(path);

  return {
    queryKey: [resource],
    queryFn: (context) => {
      const {
        queryKey: [qk],
        signal,
      } = context;

      return getSecondaryToken(axios, qk.path[1], { signal });
    },
    ...options,
  };
};

type PageParam = string | undefined;

type ServiceAccountsInfiniteQuery<
  TData = InfiniteData<GetServiceAccountsResponse, PageParam>,
> = WithRequired<
  WithInfiniteSelect<
    InfiniteQueryObserverOptions<
      GetServiceAccountsResponse,
      DefaultError,
      TData,
      GetServiceAccountsResponse,
      [Resource<Readonly<['service-accounts']>, GetServiceAccountsParams>],
      PageParam
    >
  >,
  'queryFn' | 'queryKey'
>;

type ServiceAccountsInfiniteQueryOptions<TData> = WithParams<
  Omit<
    ServiceAccountsInfiniteQuery<TData>,
    | 'queryFn'
    | 'queryKey'
    | 'getNextPageParam'
    | 'getPreviousPageParam'
    | 'initialPageParam'
  >,
  GetServiceAccountsParams
>;

export const serviceAccountsInfiniteQuery = <
  TData = InfiniteData<GetServiceAccountsResponse, PageParam>,
>(
  axios: AxiosInstance,
  options: ServiceAccountsInfiniteQueryOptions<TData> = {}
): ServiceAccountsInfiniteQuery<TData> => {
  const path = ['service-accounts'] as const;
  const { params, ...queryOpts } = options;
  const resource = getResource(path, { infinite: true, params });

  return {
    queryKey: [resource],
    queryFn: (context) => {
      const {
        pageParam,
        queryKey: [qk],
        signal,
      } = context;

      return getServiceAccounts(axios, {
        signal,
        params: {
          ...qk.params,
          page: pageParam ? Number(pageParam) : 1,
        },
      });
    },
    getNextPageParam: (page) =>
      page.next
        ? new URL(page.next).searchParams.get('page') ?? undefined
        : undefined,
    getPreviousPageParam: (page) =>
      page.previous
        ? new URL(page.previous).searchParams.get('page') ?? undefined
        : undefined,
    initialPageParam: undefined,
    ...queryOpts,
  };
};

type ServiceAccountHistoryQuery<TData = ServiceAccountHistory> = WithRequired<
  QueryObserverOptions<
    ServiceAccountHistory,
    DefaultError,
    TData,
    ServiceAccountHistory,
    [
      Resource<
        Readonly<['service-accounts', string, 'history']>,
        ServiceAccountHistoryParams
      >,
    ]
  >,
  'queryFn' | 'queryKey'
>;

export const serviceAccountHistoryQuery = <TData = ServiceAccountHistory>(
  axios: AxiosInstance,
  id: string,
  options: WithParams<
    Omit<ServiceAccountHistoryQuery<TData>, 'queryFn' | 'queryKey'>,
    ServiceAccountHistoryParams
  > = {}
): ServiceAccountHistoryQuery<TData> => {
  const path = ['service-accounts', id, 'history'] as const;
  const { params, ...queryOpts } = options;
  const resource = getResource(path, { params });

  return {
    queryKey: [resource],
    queryFn: (context) => {
      const {
        queryKey: [qk],
        signal,
      } = context;

      return getServiceAccountHistory(axios, qk.path[1], {
        params: qk.params,
        signal,
      });
    },
    ...queryOpts,
  };
};

class Queries {
  public serviceAccountQuery: <TData = ServiceAccount>(
    ...args: Drop<Parameters<typeof serviceAccountQuery<TData>>>
  ) => ServiceAccountQuery<TData>;
  public primaryTokenQuery: <TData = ServiceAccountTokenResponse>(
    ...args: Drop<Parameters<typeof primaryTokenQuery<TData>>>
  ) => PrimaryTokenQuery<TData>;
  public secondaryTokenQuery: <TData = ServiceAccountTokenResponse>(
    ...args: Drop<Parameters<typeof secondaryTokenQuery<TData>>>
  ) => SecondaryTokenQuery<TData>;
  public serviceAccountsInfiniteQuery: <
    TData = InfiniteData<GetServiceAccountsResponse, PageParam>,
  >(
    ...args: Drop<Parameters<typeof serviceAccountsInfiniteQuery<TData>>>
  ) => ReturnType<typeof serviceAccountsInfiniteQuery<TData>>;
  public serviceAccountHistoryQuery: <TData = ServiceAccountHistory>(
    ...args: Drop<Parameters<typeof serviceAccountHistoryQuery<TData>>>
  ) => ServiceAccountHistoryQuery<TData>;

  constructor(axios: AxiosInstance) {
    this.serviceAccountQuery = curry1(serviceAccountQuery)(axios);
    this.primaryTokenQuery = curry1(primaryTokenQuery)(axios);
    this.secondaryTokenQuery = curry1(secondaryTokenQuery)(axios);
    this.serviceAccountsInfiniteQuery = curry1(serviceAccountsInfiniteQuery)(
      axios
    );
    this.serviceAccountHistoryQuery = curry1(serviceAccountHistoryQuery)(axios);
  }
}

export type ServiceAccountQueries = Queries;

export const defServiceAccountQueries = (
  axios: AxiosInstance
): ServiceAccountQueries => {
  return new Queries(axios);
};
